import { useContext, useEffect, useState } from "react";

// context
import context from "../../store/context";

// assets
import Logo from "../../assets/logowhite.svg";
import CBELogo from "../../assets/CBE.png";
import LoginPattern from "../../assets/login-pattern.svg";

// icons
import CheckIcon from "../icons/Check";
import CloseIcon from "../icons/Close";

// ui
import AlertModal from "./AlertModal";

const FEATURES = [
  "Unlimited Tenders",
  "Email Alerts",
  "SMS Alerts",
  "Telegram Alerts",
  "Save Tenders",
  "4 Languages",
  "Nationwide Coverage",
  "24/7 Support",
];

const PLANS = [
  { id: "3", time: "1 Year", price: "2,360", perMonth: "197", badge: "Best Value" },
  { id: "2", time: "6 Months", price: "1,480", perMonth: "247" },
  { id: "1", time: "3 Months", price: "980", perMonth: "327" },
];

const MINIAPP_ENDPOINT = process.env.REACT_APP_MINIAPP_ENDPOINT;
const APP_NAME = "iChereta";

const Paywall = () => {
  const { setShowPaywall } = useContext(context);

  const [selected, setSelected] = useState("3");
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);

  const close = () => setShowPaywall(false);

  // stop the spinner and surface a friendly failure message
  const failPayment = (
    message = "Your payment didn't go through. Please try again."
  ) => {
    setPaying(false);
    setPayError({ title: "Payment Unsuccessful", message });
  };

  // 4. verify the payment with our backend, which marks the invoice paid
  const confirmPayment = (referenceId, ftNumber) => {
    const userToken = sessionStorage.getItem("token");

    fetch(`${MINIAPP_ENDPOINT}/api/miniapp/cbe-superapp-payment-confirm`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      body: JSON.stringify({ referance_id: referenceId, ft_number: ftNumber }),
    })
      .then((res) => res.json().catch(() => null))
      .then((json) => {
        console.log("confirm", json);
        setPaying(false);
        setShowPaywall(false);
        // backend confirmed & marked the invoice paid → refresh active status
        window.location.reload();
      })
      .catch((error) => {
        console.log("error", error);
        // the charge succeeded but our verify call failed — don't scare the
        // user with a "failed" message since money may have left their account
        setPaying(false);
        setPayError({
          title: "Almost There",
          message:
            "Your payment was received but we couldn't confirm it just yet. It may take a moment to reflect — please check back shortly.",
        });
      });
  };

  // 3. hand the signed order to the SuperApp for the native charge
  const initiatePayment = (order) => {
    // backend may wrap the order under `data`
    const payload = (order && order.data) || order || {};
    const orderPayload = payload.orderPayload || payload.order_payload;
    const authPayload = payload.authPayload || payload.auth_payload;

    if (!orderPayload || !authPayload) {
      console.log("Missing order/auth payload from backend:", order);
      failPayment();
      return;
    }

    // the backend returns authPayload.xAccessToken empty on purpose — it
    // expects us to fill it with the CBE access token captured at login
    const finalAuthPayload = {
      ...authPayload,
      xAccessToken:
        authPayload.xAccessToken || sessionStorage.getItem("cbeAccessToken") || "",
    };

    window.handlePaymentResult = (result) => {
      console.log("Payment Result:", result);
      const data = (result && result.data) || {};
      const succeeded =
        result &&
        (result.status === "success" ||
          data.transaction_status === "paid" ||
          data.ft_number);

      // verify server-side with the merchant reference + ft_number before unlocking
      if (succeeded && data.merchant_reference) {
        confirmPayment(data.merchant_reference, data.ft_number);
      } else {
        // SDK reported a cancelled / failed / incomplete payment
        failPayment();
      }
    };

    window.cbesuperapp.send(
      JSON.stringify({
        functionName: "initiatePayment",
        params: {
          orderPayload,
          authPayload: finalAuthPayload,
          callbackName: "handlePaymentResult",
          appName: payload.appName || APP_NAME,
        },
        callbackName: "handlePaymentResult",
      })
    );
  };

  // 2. ask our backend to create the signed order for the chosen plan
  const createOrder = (accessToken) => {
    const userToken = sessionStorage.getItem("token");

    fetch(`${MINIAPP_ENDPOINT}/api/miniapp/cbe-superapp-pay`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      body: JSON.stringify({
        plan_id: Number(selected),
        xAccessToken: accessToken,
      }),
    })
      .then((res) => res.json().catch(() => null))
      .then((json) => {
        console.log("order", json);
        if (json && (json.status === undefined || json.status)) {
          initiatePayment(json);
        } else {
          failPayment();
        }
      })
      .catch((error) => {
        console.log("error", error);
        failPayment();
      });
  };

  // 1. Pay Now → reuse the CBE access token captured at login
  const subscribe = () => {
    // the SuperApp is still required for the native charge in step 3
    if (!window.cbesuperapp || typeof window.cbesuperapp.send !== "function") {
      return;
    }

    setPaying(true);
    const accessToken = sessionStorage.getItem("cbeAccessToken");
    createOrder(accessToken);
  };

  // clean up the SuperApp callback when the paywall unmounts
  useEffect(() => {
    return () => {
      delete window.handlePaymentResult;
    };
  }, []);

  const selectedPlan = PLANS.find((p) => p.id === selected);

  return (
    <div className="fixed inset-0 z-[150] bg-[#352a66] flex flex-col animate-sheet-fade">
      <div className="flex-1 overflow-y-auto flex flex-col">
        {/* dark indigo hero */}
        <div className="relative shrink-0 text-white px-6 pt-10 pb-7 text-center bg-gradient-to-b from-[#1f1748] to-[#352a66]">
          {/* halftone pattern texture */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `url(${LoginPattern})`,
              backgroundSize: "cover",
              backgroundPosition: "bottom",
              backgroundRepeat: "no-repeat",
              opacity: 0.22,
            }}
          />
          <button
            onClick={close}
            aria-label="Close"
            className="absolute top-5 right-5 z-10 w-9 h-9 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center active:scale-95 transition-transform"
          >
            <CloseIcon styles="w-5 h-5" />
          </button>
          <div className="relative">
            <img src={Logo} alt="iChereta" className="h-8 mx-auto mb-3" />
            <h1 className="text-[24px] font-bold leading-tight">
              Unlock Full Access
            </h1>
            <p className="mt-1 text-white/80 text-[13px]">
              All tenders. All channels. All over Ethiopia.
            </p>
          </div>
        </div>

        {/* content cutout — rounded top curving into the indigo hero */}
        <div className="flex-1 rounded-t-[28px] bg-gray-50 px-5 pt-6 pb-6">
          {/* features */}
          <div className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
              {FEATURES.map((f) => (
                <div key={f} className="flex items-start gap-2">
                  <span className="w-4 h-4 mt-[1px] rounded-full bg-green-500/10 flex items-center justify-center shrink-0">
                    <CheckIcon styles="w-2.5 h-2.5 text-green-600" />
                  </span>
                  <span className="text-[13px] text-gray-700 leading-snug">
                    {f}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* plans */}
          <div className="mt-6">
            <h2 className="px-1 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
              Choose your plan
            </h2>
            <div className="flex flex-col gap-3">
            {PLANS.map((plan) => {
              const active = selected === plan.id;
              return (
                <button
                  key={plan.id}
                  onClick={() => setSelected(plan.id)}
                  className={`w-full flex items-center gap-3 p-4 rounded-2xl border text-left transition-colors ${
                    active
                      ? "border-[#664bc7] ring-1 ring-[#664bc7] bg-[#664bc7]/[0.05]"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <span
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                      active
                        ? "border-[#664bc7] bg-[#664bc7]"
                        : "border-gray-300"
                    }`}
                  >
                    {active && <CheckIcon styles="w-4 h-4 text-white" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">
                        {plan.time}
                      </span>
                      {plan.badge && (
                        <span className="text-[10px] font-bold uppercase tracking-wide bg-green-500 text-white px-2 py-0.5 rounded-full">
                          {plan.badge}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">
                      ≈ {plan.perMonth} birr / month
                    </span>
                  </div>
                  <span className="font-bold text-gray-900 shrink-0">
                    {plan.price}{" "}
                    <span className="text-xs font-normal text-gray-500">
                      birr
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
            <p className="px-1 pt-3 text-[11px] text-gray-400 text-center">
              All prices include 15% VAT.
            </p>
          </div>
        </div>
      </div>

      {/* sticky CTA */}
      <div className="border-t border-gray-200 bg-white/90 backdrop-blur-md px-5 pt-4 pb-[calc(env(safe-area-inset-bottom)+16px)]">
        <button
          onClick={subscribe}
          disabled={paying}
          className="w-full h-[52px] rounded-2xl text-white font-semibold text-[17px] relative overflow-hidden bg-gradient-to-b from-[#7c61e0] to-[#5b40b8] shadow-[0_4px_12px_rgba(102,75,199,0.40),inset_0_1px_0_rgba(255,255,255,0.30),inset_0_-2px_4px_rgba(74,54,149,0.55)] active:shadow-[inset_0_2px_6px_rgba(74,54,149,0.60)] active:translate-y-px transition-all duration-150 disabled:opacity-70"
        >
          {paying ? (
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-6 h-6 border-t-2 border-white rounded-full animate-spin" />
            </span>
          ) : (
            <>
              {/* fixed logo + indigo divider, anchored left so it stays put */}
              <span className="absolute left-6 top-1/2 -translate-y-1/2 flex items-center gap-3">
                <span className="w-9 h-9 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm">
                  <img
                    src={CBELogo}
                    alt="CBE"
                    className="w-6 h-6 object-contain"
                  />
                </span>
                <span className="h-6 w-px bg-[#2a1f58]/60" />
              </span>
              <span className="block text-center">
                Pay Now · {selectedPlan.price} birr
              </span>
            </>
          )}
        </button>
      </div>

      {payError && (
        <AlertModal
          title={payError.title}
          message={payError.message}
          buttonLabel="Try Again"
          onClose={() => setPayError(null)}
        />
      )}
    </div>
  );
};

export default Paywall;
